import { apiUtils } from './api';

export interface QueryLog {
  id: string;
  inputMessage: string;
  outputResponse?: string;
  generatedSQL?: string;
  sessionId?: string;
  projectId: string;
  dataSourceId?: string;
  userId: string;
  classification?: string;
  nodeUsed?: string;
  confidence?: number;
  processingTime?: number;
  tokensUsed?: number;
  resultsCount?: number;
  executionStatus: string;
  errorMessage?: string;
  enhancedQuery?: string;
  fieldContext?: any;
  userAgent?: string;
  ipAddress?: string;
  // Vector database tracking
  vectorQuery?: string;
  vectorResults?: number;
  vectorDatabase?: string;
  vectorCollection?: string;
  vectorScore?: number;
  vectorMetadata?: any;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  project: {
    id: string;
    name: string;
    description?: string;
  };
  dataSource?: {
    id: string;
    name: string;
    description?: string;
    connectorType: string;
  };
}

export interface QueryLogStatistics {
  totalQueries: number;
  avgProcessingTime: number;
  classificationStats: Array<{
    classification: string;
    count: number;
  }>;
  errorRate: number;
  successRate: number;
}

export interface QueryLogsParams {
  limit?: number;
  offset?: number;
  projectId?: string;
  userId?: string;
  dataSourceId?: string;
  startDate?: string;
  endDate?: string;
}

export const queryLogsApi = {
  // Get query logs for a project
  getProjectLogs: async (projectId: string, params?: QueryLogsParams): Promise<QueryLog[]> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    const response = await apiUtils.backend.get(`/query-logs/project/${projectId}?${queryParams}`);
    return response.data;
  },

  // Get query logs for a user
  getUserLogs: async (userId: string, params?: QueryLogsParams): Promise<QueryLog[]> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    const response = await apiUtils.backend.get(`/query-logs/user/${userId}?${queryParams}`);
    return response.data;
  },

  // Get query logs for a data source
  getDataSourceLogs: async (dataSourceId: string, params?: QueryLogsParams): Promise<QueryLog[]> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    const response = await apiUtils.backend.get(`/query-logs/datasource/${dataSourceId}?${queryParams}`);
    return response.data;
  },

  // Get query logs for a session
  getSessionLogs: async (sessionId: string): Promise<QueryLog[]> => {
    const response = await apiUtils.backend.get(`/query-logs/session/${sessionId}`);
    return response.data;
  },

  // Get query statistics
  getStatistics: async (params?: QueryLogsParams): Promise<QueryLogStatistics> => {
    const queryParams = new URLSearchParams();
    if (params?.projectId) queryParams.append('projectId', params.projectId);
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.dataSourceId) queryParams.append('dataSourceId', params.dataSourceId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    
    const response = await apiUtils.backend.get(`/query-logs/statistics?${queryParams}`);
    return response.data;
  },
};